import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { Button, type ButtonProps, DialogRoot, DialogTrigger, toast } from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import { apiClient, type SiteCapability } from '../../../state';
import { AdaptiveContentBuilderDialog } from '../../AdaptiveContent';

export interface SiteItemAdaptiveContentButtonProps extends Pick<ButtonProps, 'size'> {
    item: api.SiteSection | api.SiteSpace;
    organization: api.Organization;
    site: api.Site;
    sitePermissions: SiteCapability;
}

export function SiteItemAdaptiveContentButton(props: SiteItemAdaptiveContentButtonProps) {
    const { size, item, organization, site, sitePermissions } = props;
    const name = (() => {
        switch (item.object) {
            case 'site-section':
                return 'section';
            case 'site-space':
                return 'variant';
            default:
                assertNever(item);
        }
    })();
    const onSaveItemCondition = async (expr: string) => {
        await toast.catch(
            async () => {
                switch (item.object) {
                    case 'site-section': {
                        await apiClient.siteSections.updateSiteSectionById(
                            organization.id,
                            site.id,
                            item.id,
                            {
                                condition: expr.length > 0 ? expr : null,
                            }
                        );
                        break;
                    }
                    case 'site-space': {
                        await apiClient.siteSpaces.updateSiteSpaceById(
                            organization.id,
                            site.id,
                            item.id,
                            {
                                condition: expr.length > 0 ? expr : null,
                            }
                        );
                        break;
                    }
                    default:
                        assertNever(item);
                }
                toast.success(`Condition for ${name} updated successfully!`);
            },
            `Error while updating site ${item.object ? 'section' : 'variant'} condition`
        );
    };
    return (
        <DialogRoot>
            <DialogTrigger>
                <Button
                    kind="nested"
                    iconOnly
                    size={size}
                    leadingIcon={Icon.Condition}
                    label={`This ${name} is conditionally shown to visitors. ${sitePermissions.canAdminSite ? 'Click to edit.' : 'Only site administrators can edit this.'}`}
                    disabled={!sitePermissions.canAdminSite}
                />
            </DialogTrigger>

            <AdaptiveContentBuilderDialog
                initialTab="expression"
                organizationId={organization.id}
                siteId={site.id}
                tabs={[
                    {
                        tab: 'expression',
                        target: name,
                        expression: item.condition || undefined,
                        onSaveExpression: onSaveItemCondition,
                    },
                    {
                        tab: 'schema',
                    },
                ]}
            />
        </DialogRoot>
    );
}
